import { z } from 'zod';

/**
 * Connection Category Enum
 */
export const connectionCategorySchema = z.enum([
  'SEMANTIC_LAYER',
  'CRM',
  'DATABASE',
  'API',
  'DOCUMENT_STORE',
]);
export type ConnectionCategory = z.infer<typeof connectionCategorySchema>;

/**
 * Connection Provider Enum
 */
export const connectionProviderSchema = z.enum([
  'CUBEJS',
  'SALESFORCE',
  'POSTGRES',
  'SPECIFIC_HOST',
  'SHAREPOINT',
  'VEEVAVAULT',
]);
export type ConnectionProvider = z.infer<typeof connectionProviderSchema>;

/**
 * Connection Status Enum
 */
export const connectionStatusSchema = z.enum(['active', 'error', 'pending_test', 'disabled']);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

/**
 * Test Result Schema
 */
export const testResultSchema = z
  .object({
    success: z.boolean(),
    message: z.string(),
    timestamp: z.coerce.date(),
  })
  .nullable()
  .optional();
export type TestResult = z.infer<typeof testResultSchema>;

/**
 * Create Data Connection Request Body
 */
export const createDataConnectionBodySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  connectionCategory: connectionCategorySchema,
  connectionProvider: connectionProviderSchema,
  config: z.record(z.string(), z.unknown()).default({}),
  /**
   * Secrets for the data connection (Key-Value pairs)
   * - Omitted: No secret stored in AWS Secrets Manager
   * - Empty object `{}`: Equivalent to omitted (no secret)
   * - Non-empty `{key: "value", ...}`: Creates a secret in AWS Secrets Manager
   *
   * Example: `{ password: "db-password", apiKey: "sk-123456" }`
   */
  secrets: z.record(z.string(), z.string()).optional(),
});
export type CreateDataConnectionBody = z.infer<typeof createDataConnectionBodySchema>;

/**
 * Update Data Connection Request Body
 */
export const updateDataConnectionBodySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  /**
   * Secrets for the data connection (Key-Value pairs)
   * - Omitted: No change to existing secrets
   * - Empty object `{}`: Delete existing secret from AWS Secrets Manager
   * - Non-empty `{key: "value", ...}`: Create or update secret in AWS Secrets Manager
   *
   * Example: `{ password: "new-password", apiKey: "sk-987654" }`
   */
  secrets: z.record(z.string(), z.string()).optional(),
});
export type UpdateDataConnectionBody = z.infer<typeof updateDataConnectionBodySchema>;

/**
 * Data Connection Response (single)
 */
export const dataConnectionResponseSchema = z.object({
  id: z.number(),
  identifier: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  connectionCategory: connectionCategorySchema,
  connectionProvider: connectionProviderSchema,
  config: z.record(z.string(), z.unknown()),
  status: connectionStatusSchema,
  testResult: testResultSchema,
  secretRef: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  createdBy: z
    .object({
      identifier: z.string(),
      username: z.string().optional(),
    })
    .nullable(),
  updatedBy: z
    .object({
      identifier: z.string(),
      username: z.string().optional(),
    })
    .nullable()
    .optional(),
});
export type DataConnectionResponse = z.infer<typeof dataConnectionResponseSchema>;

/**
 * List Data Connections Response
 */
export const listDataConnectionsResponseSchema = z.object({
  items: z.array(dataConnectionResponseSchema),
});
export type ListDataConnectionsResponse = z.infer<typeof listDataConnectionsResponseSchema>;

/**
 * Test Data Connection Request
 */
export const testDataConnectionRequestSchema = z.object({
  // No body required — test endpoint extracts identifier from URL
});

/**
 * Test Data Connection Response
 */
export const testDataConnectionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  timestamp: z.coerce.date(),
});
export type TestDataConnectionResponse = z.infer<typeof testDataConnectionResponseSchema>;
